/**
 * What a hotel's state means, and what cancelling a trip does to it.
 *
 * Written as plain JS on purpose: Deno edge functions, the Express server and
 * vitest all import THIS file, so there is one set of rules rather than a twin
 * per runtime that can drift.
 *
 * The founder's rule, 2026-09-23: if we don't have it booked on our end, the
 * customer gets 100% back as Defera Credit — no matter what the supplier's own
 * terms are. Booked-on-our-end is an admin judgement, recorded as
 * `stays.status = 'reserved'`.
 */

export const STAY_STATUS = Object.freeze({
  PENDING: 'pending',
  RESERVED: 'reserved',
  NEEDS_CANCELLATION: 'needs_cancellation',
  CANCELLED: 'cancelled',
  NOT_NEEDED: 'not_needed',
});

/** The one stay that matters on a booking, whatever shape the caller holds. */
export function pickStay(source) {
  if (!source) return null;
  if (Array.isArray(source)) return source.find((s) => s && typeof s === 'object') || null;
  if (typeof source !== 'object') return null;
  return source;
}

/**
 * Is a room actually booked on our end?
 *
 * Only an explicit `reserved` counts. A missing status (a row written before
 * migration 153) is NOT booked — that is the honest default, and it keeps the
 * customer's automatic credit path open rather than silently closing it on
 * every historical package.
 */
export function isStayBooked(stay) {
  return pickStay(stay)?.status === STAY_STATUS.RESERVED;
}

/**
 * Does this stay block the automatic 100% cancel-for-credit path?
 * Same question as isStayBooked, named for the decision it drives.
 */
export function stayBlocksAutomaticCredit(stay) {
  return isStayBooked(stay);
}

/**
 * What a trip cancellation should do to the stay row.
 *
 * Returns null when there is nothing to do — no stay, or a stay already in a
 * settled state — so callers can skip the write entirely.
 *
 * @returns {{nextStatus: string, opsActionRequired: boolean, reason: string}|null}
 */
export function stayCancelOutcome(stay) {
  const row = pickStay(stay);
  if (!row) return null;
  const status = row.status || STAY_STATUS.PENDING;
  if (status === STAY_STATUS.RESERVED) {
    return {
      nextStatus: STAY_STATUS.NEEDS_CANCELLATION,
      opsActionRequired: true,
      reason: 'room is booked with the hotel and must be cancelled by a human',
    };
  }
  if (status === STAY_STATUS.PENDING) {
    return {
      nextStatus: STAY_STATUS.CANCELLED,
      opsActionRequired: false,
      reason: 'room was never reserved, nothing to unwind',
    };
  }
  // cancelled / needs_cancellation / not_needed: already settled or already
  // flagged. Re-stamping would reset someone's in-progress ops work.
  return null;
}

/** Cents Defera stands to lose if this room is cancelled: only a reserved,
 *  non-refundable room with a known cost is a real loss. */
export function stayExposureCents(stay) {
  const row = pickStay(stay);
  if (!isStayBooked(row)) return 0;
  if (row.is_refundable === true) return 0;
  const cents = Number(
    row.actual_cost_cents ?? (row.total_amount != null ? Math.round(Number(row.total_amount) * 100) : NaN),
  );
  return Number.isFinite(cents) && cents > 0 ? Math.round(cents) : 0;
}
